import {
  Block,
  Role,
  isBlockWithChildren,
  isCommentBlock,
  isComponentBlock,
  isDocumentBlock,
  isHidableBlock,
  isInstanceBlock,
  isListBlock,
  isMediaBlock,
  isRichTextBlock,
  isReferenceBlock,
  shouldInheritHiddenState,
} from '@basehub/data/replicache/index'
import type { TreeManagerMethods } from '@basehub/utils/tree/index'
import { EditorMode } from '../../editor-mode-provider'
import { editorCanEditBlocks } from '@basehub/data/replicache/schema/block/permissions'

export function getAllowedMenuActions({
  block,
  parentBlock,
  userRole,
  droppingIntoBlock,
  editorMode,
  treeManager,
  path,
}: {
  block: Block
  parentBlock: Block | undefined
  userRole: Role
  droppingIntoBlock?: Block
  treeManager: TreeManagerMethods<Block, NonNullable<unknown>>
  editorMode: EditorMode
  path: string
}) {
  const canUpdate =
    editorMode === 'write:all' ||
    (['editor', 'owner', 'member'].includes(userRole) &&
      editorCanEditBlocks({ blocks: [block], parentBlock }))

  let canReorder =
    canUpdate &&
    !!parentBlock &&
    !isRichTextBlock(parentBlock) &&
    !isInstanceBlock(parentBlock)
  if (droppingIntoBlock && canReorder) {
    if (
      editorMode === 'write:value-only' ||
      editorMode === 'write:value-and-title'
    ) {
      if (parentBlock === droppingIntoBlock) {
        canReorder = true
      } else {
        canReorder = false
      }
    } else {
      canReorder = true
    }
  }

  let addBelow: 'instance' | boolean = false
  if (
    editorMode === 'write:all' &&
    !isInstanceBlock(block) &&
    !isRichTextBlock(block)
  ) {
    if (isListBlock(block) || isReferenceBlock(block)) {
      addBelow = 'instance'
    } else {
      addBelow = true
    }
  }

  return {
    copy: true,
    paste:
      (isDocumentBlock(block) ||
        isComponentBlock(block) ||
        isListBlock(block) ||
        isReferenceBlock(block)) &&
      editorMode === 'write:all',
    'copy-file-link': isMediaBlock(block) && block.value.status === 'uploaded',
    'wrap-in-collection': editorMode === 'write:all',
    rename: canUpdate,
    delete: canUpdate,
    duplicate: canUpdate,
    favorite: isBlockWithChildren(block),
    configure:
      editorMode === 'write:all' &&
      parentBlock &&
      isInstanceBlock(parentBlock) &&
      !isCommentBlock(block),
    'add-below': addBelow,
    'toggle-hidden':
      canUpdate &&
      !!parentBlock &&
      !shouldInheritHiddenState(
        block,
        path,
        (id) =>
          treeManager.getNode(id, {
            fallbackKey: 'sourceBlockId',
            required: true,
          }).value
      ) &&
      (isHidableBlock(block) || block.hidden),
    reorder: canReorder,
  } as const
}
